import { api } from '@/lib/api-client'
import type { AdminRating, DriverRatingStats, PageResponse, RatingFilters, RatingSummary } from '@/types/api'

/**
 * Customer ratings, read-only.
 *
 * A rating is the customer's word and the office does not edit it. What the
 * office does is read the low ones, see which drivers they cluster on, and act
 * outside this system — so there is nothing here but reads.
 */

export function fetchRatings(filters: RatingFilters): Promise<PageResponse<AdminRating>> {
  return api.get<PageResponse<AdminRating>>('/api/admin/ratings', { query: { ...filters } })
}

/**
 * The headline figures over exactly the filter the list is showing.
 *
 * Paging parameters are stripped: the summary is about the whole filtered set,
 * and sending `page` would only make the cache key differ per page for an
 * answer that never changes between them.
 */
export function fetchRatingSummary(filters: RatingFilters): Promise<RatingSummary> {
  const { page: _page, size: _size, sort: _sort, direction: _direction, ...rest } = filters
  return api.get<RatingSummary>('/api/admin/ratings/summary', { query: { ...rest } })
}

/** Every driver with a rating in the window, best average first. */
export function fetchDriverRatingStats(window: { from?: string; to?: string }): Promise<DriverRatingStats[]> {
  return api.get<DriverRatingStats[]>('/api/admin/ratings/drivers', { query: { ...window } })
}

/**
 * The rating on one trip, or null when the customer has not rated it.
 *
 * The server answers an unrated trip with 204, which the client turns into
 * `undefined`; that becomes `null` here because React Query treats undefined
 * data as "not loaded" and would refetch forever.
 */
export async function fetchRatingForTrip(tripId: number): Promise<AdminRating | null> {
  const rating = await api.get<AdminRating | undefined>(`/api/admin/ratings/for-trip/${tripId}`)
  return rating ?? null
}
